/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { merge } from 'lodash';
import { KeyValue, unbox } from 'ngrx-forms';
import {
  concatMap,
  EMPTY,
  from,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  ConfirmServiceInterface,
  filterNullish,
  LazyEntityFacadeInterface,
  mapToFilterModel,
  NavigationFacadeInterface,
  RouterFacadeInterface,
  SharedEntityFacadeInterface,
  ToastServiceInterface,
} from '@ideapa/app-common';
import {
  keyboardAdd,
  keyboardApplyFilters,
  keyboardDelete,
  keyboardOpenFilters,
  keyboardSave,
  keyboardResetFilters,
} from '@ideapa/app-keyboard';
import { BaseDto } from '@ideapa/common-dto';
import { DeepPartial, TAB_CLOSE_MESSAGE } from '@ideapa/common-utils';
import {
  Actions,
  createEffect,
  CreateEffectMetadata,
  ofType,
} from '@ngrx/effects';

import { LazyEntityActions } from './models';
import { FilterValue } from '@ideapa/common-interfaces';

export abstract class BaseLazyEntityEffects<
  T extends BaseDto,
  V,
  K extends KeyValue
> {
  constructor(
    protected actions$: Actions,
    protected facade: LazyEntityFacadeInterface<T, K>,
    protected sharedFacade: SharedEntityFacadeInterface<T>,
    protected toastService: ToastServiceInterface,
    protected navigationFacade: NavigationFacadeInterface,
    protected confirmService: ConfirmServiceInterface,
    protected entityName: string,
    protected actions: LazyEntityActions<T, K>,
    protected routerFacade: RouterFacadeInterface
  ) {}

  keyboardSave$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardSave),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(
              this.facade.form$,
              this.facade.entityId$,
              this.facade.canSave$
            )
          )
        ),
        tap(([action, form, entityId, canSave]) => {
          if (
            canSave &&
            entityId != null &&
            action.entity === this.entityName &&
            form.isDirty
          ) {
            this.facade.formSave(form);
          }
        })
      ),
    { dispatch: false }
  );

  keyboardDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardDelete),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(this.facade.entityId$, this.facade.canDelete$)
          )
        ),
        tap(([action, entityId, canDelete]) => {
          if (
            canDelete &&
            entityId != null &&
            entityId !== 0 &&
            action.entity === this.entityName
          ) {
            this.facade.formDelete();
          }
        })
      ),
    { dispatch: false }
  );

  keyboardAdd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardAdd),
        concatMap((a) =>
          of(a).pipe(withLatestFrom(this.facade.entityId$, this.facade.canAdd$))
        ),
        tap(([action, entityId, canAdd]) => {
          if (canAdd && entityId == null && action.entity === this.entityName) {
            this.facade.addEntity();
          }
        })
      ),
    { dispatch: false }
  );

  openFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardOpenFilters),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(
              this.facade.entityId$,
              this.routerFacade.isFindMode$,
              this.routerFacade.isModalListMode$
            )
          )
        ),
        tap(([action, entityId, isFindMode, isModalListMode]) => {
          if (
            !isFindMode &&
            !isModalListMode &&
            entityId == null &&
            action.entity === this.entityName
          ) {
            this.facade.showAdvancedFilters(true);
          }
        })
      ),
    { dispatch: false }
  );

  applyFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardApplyFilters),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(
              this.facade.entityId$,
              this.facade.showAdvancedFilters$,
              this.facade.filterForm$
            )
          )
        ),
        tap(([action, entityId, visibleFilters, filterForm]) => {
          if (
            entityId == null &&
            action.entity === this.entityName &&
            visibleFilters
          ) {
            const filters = unbox(filterForm.value.filters);
            this.facade.setFilterModel(mapToFilterModel(filters));
            this.facade.showAdvancedFilters(false);
          }
        })
      ),
    { dispatch: false }
  );

  resetFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(keyboardResetFilters),
        concatMap((a) => of(a).pipe(withLatestFrom(this.facade.entityId$))),
        tap(([action, entityId]) => {
          if (entityId == null && action.entity === this.entityName) {
            const filters: FilterValue[] = [];
            this.facade.setFilterModel(mapToFilterModel(filters));
            this.facade.showAdvancedFilters(false);
          }
        })
      ),
    { dispatch: false }
  );

  formSave$: Observable<any> & CreateEffectMetadata = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.formSave),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(this.facade.entity$, this.facade.queryParams$)
          )
        ),
        tap(([action, entity, queryParams]) => {
          if (!action.form.isValid) {
            this.facade.displayFormError();
          } else {
            const id = entity?.id;

            const param = queryParams['param']
              ? JSON.parse(atob(queryParams['param']))
              : undefined;

            const dto: DeepPartial<T> = this.mapToDto(
              action.form.value,
              entity,
              param
            );

            this.upsertEntity(id, dto, action.callback, param);
          }
        })
      ),
    { dispatch: false }
  );

  addEntity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.addEntity),
        tap((action) => {
          this.routerFacade.navigateRelative([0]);
        })
      ),
    { dispatch: false }
  );

  selectItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.selectItem),
        concatMap((a) =>
          of(a).pipe(withLatestFrom(this.routerFacade.channelId$))
        ),
        tap(([action, channelId]) => {
          if (channelId) {
            this.routerFacade.notifySelectedItemToOpener(
              action.item,
              channelId
            );
          }
        })
      ),
    { dispatch: false }
  );

  formDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.formDelete),
        concatMap((a) =>
          of(a).pipe(
            withLatestFrom(this.facade.getLoadedEntity$.pipe(filterNullish()))
          )
        ),
        switchMap(([action, entity]) => {
          return from(
            this.confirmService.confirm(
              `Confermi di voler eliminare l'elemento${
                (<any>entity).codice
                  ? ' con codice ' + (<any>entity).codice
                  : ''
              }?`
            )
          ).pipe(
            tap((result) => {
              if (result) {
                if (entity.id) {
                  this.sharedFacade.delete(entity.id, true, true, () =>
                    this.facade.clearEntityId()
                  );
                }
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  formReset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.addSuccess, this.actions.updateSuccess),
        tap((action) => {
          this.facade.resetForm();
        })
      ),
    { dispatch: false }
  );

  clearCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.resetState),
        tap(() => {
          this.sharedFacade.clearCache();
        })
      ),
    { dispatch: false }
  );

  displayFormError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.displayFormError),
        tap(() => {
          this.toastService.errorFeedback(
            'Alcuni campi obbligatori non sono stati compilati, o non sono compilati correttamente.'
          );
        })
      ),
    { dispatch: false }
  );

  downloadExcel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.downloadExcel),
        concatMap(
          (a) => of(a)
          // .pipe(
          //   withLatestFrom(
          //     this.facade.getFilterModel$,
          //     this.facade.getColumnState$
          //   )
          // )
        ),
        tap((action) => {
          this.sharedFacade.downloadExcel(
            action.fileName,
            action.columns,
            action.filters,
            action.orderBy,
            action.join
          );
        })
      ),
    { dispatch: false }
  );

  tabClose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.manualClose),
        withLatestFrom(this.facade.form$),
        mergeMap(([action, form]) => {
          if (action.path !== this.entityName) {
            return of(EMPTY);
          }
          if (form.isDirty) {
            return from(this.confirmService.confirm(TAB_CLOSE_MESSAGE)).pipe(
              tap((result) => {
                if (result) {
                  this.navigationFacade.closeTab(action.path);
                  this.navigationFacade.navigateToFirstAvailableTab();
                }
              })
            );
          } else {
            this.navigationFacade.closeTab(action.path);
            this.navigationFacade.navigateToFirstAvailableTab();
            return of(EMPTY);
          }
        })
      ),
    { dispatch: false }
  );

  protected mapToDto(
    formValue: K,
    entity: T | undefined,
    param?: DeepPartial<T>
  ): DeepPartial<T> {
    return {} as DeepPartial<T>;
  }

  protected upsertEntity(
    id: number | undefined,
    dto: DeepPartial<T>,
    callback: ((item: T) => void) | undefined,
    param?: DeepPartial<T>
  ) {
    if (id && id != 0) {
      this.sharedFacade.update(
        id,
        {
          id: id,
          ...dto,
        },
        callback,
        true,
        false
      );
    } else {
      if (param) {
        merge(dto, param);
      }
      this.sharedFacade.add(
        {
          ...dto,
        },
        true,
        false
      );
    }
  }
}
